import type { TProduct } from "../../../data";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";

export const knownStatusList = ["hybrid_broadcast", "listen", "broadcast", "private", "eod"] as const;

export type TKnownStatus = (typeof knownStatusList)[number];

export type TStatus = TKnownStatus | (string & {});
export type TManualStatus = "private" | "broadcast" | "hybrid_broadcast";

export type TStorageType = "broadcast" | "local" | "eod" | "global" | "shared";

export type TManualStoreStorageType = "shared" | "broadcast" | "local" | "global";

export const statusMap = {
  hybrid_broadcast: "shared",
  listen: "shared",
  private: "local",
  broadcast: "broadcast",
  eod: "eod",
} satisfies Record<TKnownStatus, TStorageType>;

export function isPrivateStorageType(status: TStatus = "listen") {
  return status === "private";
}

export function isSharedStorageType(status: TStatus = "listen") {
  return status === "hybrid_broadcast" || status === "listen" || status === "broadcast";
}

export function isEodStorageType(status: TStatus = "listen") {
  return status === "eod";
}

export function isGlobalStorageType(status: TStatus = "listen") {
  return !knownStatusList.includes(status);
}

export const statusMapVals = Object.values(statusMap);

export function getInstrumentType(status: TStatus = "listen") {
  return knownStatusList.includes(status) ? statusMap[status as (typeof knownStatusList)[number]] : "global";
}

export function getManualInstrumentType(status: TManualStatus | string = "listen") {
  if (status === "private" || status === "broadcast" || status === "hybrid_broadcast") {
    return statusMap[status];
  }
  return null;
}

export function statusId(productId: string, status: TStatus) {
  return `${productId}:${getInstrumentType(status)}`;
}

export type TProductWithGridIdAndStatus = TProduct & {
  gridId: string | undefined;
  status: string | undefined;
};

export type TAffectedProducts = {
  "current-page"?: TProductWithGridIdAndStatus[];
  "all-pages"?: TProductWithGridIdAndStatus[];
};

export function joinProductsWithGridPageProducts(
  products: TProduct[] | null | undefined,
  pageProducts: PageProductsFragmentFragment[],
  statusMap: Record<string, TStatus>,
) {
  return pageProducts
    ?.map((prod) => {
      const matchingProduct = products?.find((p) => p.id === prod.product_id);

      if (!matchingProduct) {
        return;
      }

      return {
        ...matchingProduct,
        id: prod.product_id ?? matchingProduct.id,
        gridId: prod.grid_id ?? prod.id,
        status: prod.product_id ? statusMap?.[prod.product_id] : "listen",
      };
    })
    .filter(Boolean)
    .filter((product) => product.has_shared_cell) satisfies TProductWithGridIdAndStatus[] | undefined;
}
